/* eslint-disable max-lines */
/**
 * Global modals that only make sense on a cloud build — trials, paywalls,
 * upgrade and downgrade flows, Kinde migration, team onboarding — together with
 * the billing/entitlement state that decides which of them the modal queue may
 * enqueue.
 *
 * Mounted by `GlobalModals` behind `!isOnPrem`. It is the exception, not the
 * default: a global modal that does NOT read cloud billing, subscription, trial
 * or entitlement state belongs in `UniversalGlobalModals`, which mounts on every
 * build. Adding one here that could have gone there is how on-prem lost its
 * idle logout and every desktop recording-recovery surface.
 */
import { useTrackPreviousSettingsRoute } from '@/app-shell/settings/hooks/useTrackPreviousSettingsRoute';
import {
  hasShownAffiliateLinkModalAtom,
  shouldShowAffiliateLinkModalAtom,
} from '@/atoms/affiliateLinkAtoms';
import { firmwareStartupSeenAtom } from '@/atoms/chronicle/firmware';
import { loadDistributionTagAtom } from '@/atoms/distributionTagAtoms';
import { IS_EVIDENCE_COPY_CONFIRMATION_ENABLED } from '@/atoms/evidenceCopyConfirmationAtoms';
import { evidenceSidebarOpenAtom } from '@/atoms/evidenceSidebarAtoms';
import { formViewModalOpenAtom } from '@/atoms/formFillingAtom';
import { trialExtensionModalOpenAtom } from '@/atoms/freeTrialModalAtoms';
import { hasIntegrationAtom } from '@/atoms/integrationAtoms';
import { appStateForModalsAtom } from '@/atoms/launchModalsConfigAtom';
import { isOnboardingSubmitInProgressAtom } from '@/atoms/onboardingAtoms';
import { PATIENT_LIST_INTRODUCTION_MODAL_ID } from '@/atoms/patient-list-modal-ids';
import { SESSION_MERGE_ONBOARDING_SETTINGS_MODAL_ID } from '@/atoms/session-merge-onboarding-modal-ids';
import { ROUTE_TO_SIDEBAR_CONTENT } from '@/atoms/sidebarPanelAtoms';
import {
  hasCompletedOnboardingAtom,
  hasVisitedNewUserRouteAtom,
} from '@/atoms/userCreationAtoms';
import { ClinicianMaxUpgradeModal } from '@/components/modal/clinician-max-upgrade-modal';
import { ClinicianUpgradeModal } from '@/components/modal/clinician-modal';
import { ClinicianTrialModal } from '@/components/modal/clinician-trial-modal';
import { DictationPremiumTrialModal } from '@/components/modal/dictation-premium-trial-modal/premium-trial-modal';
import LegacyOnboardingRequiredUserDetailsModal from '@/components/modal/enter-required-user-details';
import { EvidenceCopyConfirmationPopover } from '@/components/modal/evidence-copy-confirmation-popover';
import { EvidencePowerUserUpgradeModal } from '@/components/modal/evidence-power-user-modal';
import { HealthNzMigrationModal } from '@/components/modal/health-nz-migration-modal';
import { KindeMigrationModal } from '@/components/modal/kinde-migration-modal';
import { PatientListIntroductionModal } from '@/components/modal/patient-list-introduction-modal';
import { PendingTeamInviteModal } from '@/components/modal/pending-team-invite-modal/pending-team-invite-modal';
import { PrechartBetaIntroModal } from '@/components/modal/prechart-beta-intro-modal';
import { ScribeOnboardingModal } from '@/components/modal/scribe-onboarding-modal';
import { TrialEndPlanSelectionModal } from '@/components/modal/trial-end-plan-selection-modal';
import {
  computeIntegrationAndNeedsUpgrade,
  computeShouldShowLegacyOnboardingDetailsModal,
  computeShouldShowOnboardingModal,
  computeShouldShowReverseTrialModals,
  computeShouldShowUserSelfOnboardingModal,
  computeShowLegacyTrialEndModal,
} from '@/components/utils/global-modals-cloud-only.conditions';
import { useIsDictationEnabled } from '@/features/dictation/hooks/useIsDictationEnabled';
import { NewInThisReleasePopup } from '@/features/dictation/new-in-this-release-card';
import {
  useIsAutoLinkSessionsEnabled,
  useIsDesktopDictationFlagEnabled,
  useIsDictateDeviceDetectedPopupEnabled,
  useIsDictateDeviceSetupEnabled,
  useIsDictateReengagementCardEnabled,
  useIsFirmwareStartupCheckEnabled,
  useIsHealthNzMigrationPromptEnabled,
  useIsHeidi2OptInEnabled,
  useIsNewSessionListEnabled,
  useIsPatientListEnabled,
  useIsPendingTeamInvitePromptEnabled,
  useIsProjectsEnabled,
  useIsSessionPageV2Enabled,
  useIsShareSessionAnnounceSpotlightEnabled,
  useIsTeamHeidi2AccessControlsEnabled,
  useIsWebDictationFlagEnabled,
  useIsWebDictationOnboardingFlagEnabled,
} from '@/features/flags';
import { practiceTrialModalPendingAtom } from '@/features/growth/practice-invite-team-members/atoms';
import { useIsGamifyTrialArmAllowed } from '@/features/post-note-quality/hooks/use-is-gamify-trial-arm-allowed';
import { EnableSessionMergeCardPopover } from '@/features/session-merge-onboarding/components/enable-session-merge-card';
import { LazySessionMergeEducationModal } from '@/features/session-merge-onboarding/components/session-merge-education-modal';
import { SessionMergeTabCoachmark } from '@/features/session-merge-onboarding/components/session-merge-tab-coachmark';
import { useIsShadowHeidiEnabled } from '@/features/shadowHeidi/hooks/useIsShadowHeidiEnabled';
import { ClinicianTrialEndedModal } from '@/features/trialsAndUpgrades/clinician-trial-ended-modal';
import { EvidencePostUpgradeOnboardingModal } from '@/features/trialsAndUpgrades/evidence-post-upgrade-onboarding-modal';
import { EvidencePostUpgradeWelcomePopup } from '@/features/trialsAndUpgrades/evidence-post-upgrade-welcome';
import { useTrialRecapUsageStats } from '@/features/trialsAndUpgrades/hooks/useTrialRecapUsageStats';
import { PracticeInviteTeamModal } from '@/features/trialsAndUpgrades/practice-invite-team-modal';
import { PracticeTrialModal } from '@/features/trialsAndUpgrades/practice-trial-modal';
import { TeamTrialUpgradeRequestModal } from '@/features/trialsAndUpgrades/team-trial-upgrade-request-modal/team-trial-upgrade-request-modal';
import { TrialEndDiscreetCard } from '@/features/trialsAndUpgrades/trial-end-discreet-card';
import { TrialEndReminderPopup } from '@/features/trialsAndUpgrades/trial-end-reminder-popup';
import { TrialRecapModal } from '@/features/trialsAndUpgrades/trial-recap-modal';
import { TrialUpgradeRequestCallbackModal } from '@/features/trialsAndUpgrades/trial-upgrade-request-callback-modal/trial-upgrade-request-callback-modal';
import { useRoutinesSpotlightTour } from '@/features/workflows/spotlight/useRoutinesSpotlightTour';
import { useChronicleDeviceStatus } from '@/hooks/desktop';
import { useDesktopConnectivity } from '@/hooks/desktop/useDesktopConnectivity';
import { useIsEhrLaunch } from '@/hooks/integrations/useIsEhrLaunch';
import { useIsWidgetAuthFlow } from '@/hooks/integrations/useIsWidgetAuthFlow';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { useQueryAppleMigrationTicket } from '@/hooks/query/useQueryAppleMigrationTicket';
import { useQueryCreditAllowance } from '@/hooks/query/useQueryCreditAllowance';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useQuerySelfOnboardingTeams } from '@/hooks/query/useQuerySelfOnboardingTeams';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useDictateOnboardingStoreAccess } from '@/hooks/useDictateOnboardingStoreAccess';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useIsClinicalRecordingActive } from '@/hooks/useIsClinicalRecordingActive';
import { useIsEvidencePowerUserEnabled } from '@/hooks/useIsEvidencePowerUserEnabled';
import { useIsLinkSharingAllowed } from '@/hooks/useIsLinkSharingAllowed';
import useReverseTrial from '@/hooks/useReverseTrial';
import { useSessionListSpotlightTour } from '@/hooks/useSessionListSpotlightTour';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import IntegrationPaywall from '@/integrations/integration-paywall';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { type RootState } from '@/store';
import { isTauri } from '@/tauri/utils';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { isReferralProgramAvailableForLocale } from '@/utils/i18n/referralProgramLocale';
import { computeIsEligibleForKindeMigrationModal } from '@/utils/kindeAppleMigrationEligibility';
import { WORK_CHAT_HOME_PATH } from '@heidi/agents/contracts';
import {
  computeShouldQueueHeidi2EvidenceTour,
  computeShouldShowHeidi2ConfirmAccessModal,
  heidi2EvidenceTourAddMenuMountCountAtom,
  heidi2EvidenceTourHeadingHomeAtom,
  heidi2EvidenceTourRailMountedAtom,
  heidi2EvidenceTourStepAtom,
  useHeidi2ExploreLaterState,
} from '@heidi/agents/data';
import {
  computeShouldQueueSideNavCreditUsageThresholdPopover,
  creditAllowanceThresholdConsumedAtom,
  DowngradeRetentionFlowModals,
  isPreviousTrialNinetyDayTrial,
  PracticeUpgradeModal,
  PricingPlansModal,
  useIsCreditUsageSurfaceEnabled,
  useWorkspaceIdSettledForCreditUsagePopoverLaunch,
} from '@heidi/commerce';
import { useDictationLogs } from '@heidi/dictate/data';
import { isScribeLayoutCsatEligible } from '@heidi/growth';
import { ModalId, SignupSource, SubscriptionTier } from '@heidi/schema/ml-scribe';
import {
  useCanPromptSessionMergeOnboarding,
  useOpenSessionHasMergeTab,
} from '@heidi/session';
import { useQueryPendingTeamInvitations } from '@heidi/workspace/data';
import { differenceInDays } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import { isEmpty } from 'lodash-es';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { type ComponentProps, type ComponentType, memo, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  IS_DICTATION_LOGS_ENABLED,
  IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED,
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
  IS_ONBOARDING_V2_TREATMENT,
  IS_PRECHART_ENABLED,
  IS_SELF_ONBOARDING_MODAL_QUEUED,
} from '../constants/flags';
import AffiliateLinkModal from '../modal/affiliate-link-modal';
import { CustomTemplateOnboardingModal } from '../modal/custom-template-onboarding-modal';
import { DowngradeReconsiderModal } from '../modal/downgrade-reconsider-modal';
import { EvidenceReferralModal } from '../modal/evidence-referral-modal';
import type FormViewModalType from '../modal/form-filling-modals/view-form-modal';
import { FreeTrialModal } from '../modal/free-trial-modal';
import { GamifyTrialExtensionInviteModal } from '../modal/gamify-trial-extension-invite-modal';
import { GamifyTrialExtensionPracticeModal } from '../modal/gamify-trial-extension-practice-modal';
import { GamifyTrialExtensionTierModal } from '../modal/gamify-trial-extension-tier-modal';
import { EndTrialModal } from '../modal/heidi-pro-modal/end-trial-modal';
import { EndPromotedTrialModal } from '../modal/heidi-pro-modal/end-trial-modal/end-promoted-trial-modal';
import { ExtendFreeTrialModal } from '../modal/heidi-pro-modal/extend-free-trial';
import { HeidiProUpgradedSuccessful } from '../modal/heidi-pro-modal/heidi-pro-upgraded-sucessful';
import HeidiTogetherModal from '../modal/heidi-pro-modal/heidi-upgrade-together-modal';
import { TrialToPaidModal } from '../modal/heidi-pro-modal/trial-to-paid-modal';
import NetworkErrorModal from '../modal/network-firewall-error';
import type OnboardingModalType from '../modal/onboarding-modal';
import SectionalWritebackInformModal from '../modal/sectional-writeback-inform-modal';
import { TrialEndingSoonReminderModal } from '../modal/trial-ending-soon-reminder-modal';
import type UserSelfOnboardingModalType from '../modal/user-self-onboarding-modal';
import { GetMobileAppModalHost } from './get-mobile-app-modal-host';
import { PendingSharedTemplateImporter } from './PendingSharedTemplateImporter';
import { ScribeLayoutCsatPopoverHost } from './scribe-layout-csat-popover-host';
import { useKindePropertiesReconnectRecovery } from './useKindePropertiesReconnectRecovery';

// Dev tooling: same build-time env comparison as `_app.tsx` so the bundler folds
// the branch away and the debugger tree never reaches a production bundle.
if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production') {
  void import('@/lib/modal-debugger');
}

// The Heidi II confirm-access modal lives behind the `@heidi/agents` UI door,
// and nothing in `_app`'s eager graph may import that door. Its app mount is a
// module of its own, loaded here across a split point.
const Heidi2ConfirmAccessModal = dynamic(
  () => import('./global-modals-cloud-only-heidi-2-confirm-access'),
  { ssr: false }
);

const FormViewModal: ComponentType<ComponentProps<typeof FormViewModalType>> = dynamic(
  () => import('../modal/form-filling-modals/view-form-modal'),
  {
    ssr: false,
  }
);

const UserSelfOnboardingModal: ComponentType<
  ComponentProps<typeof UserSelfOnboardingModalType>
> = dynamic(() => import('../modal/user-self-onboarding-modal'), {
  ssr: false,
});

const OnboardingModal: ComponentType<ComponentProps<typeof OnboardingModalType>> =
  dynamic(() => import('../modal/onboarding-modal'), {
    ssr: false,
  });

const isTauriBuild = process.env.NEXT_PUBLIC_IS_TAURI === 'true';

// Build-time flag (not runtime isTauri()) so Turbopack drops the desktop-only
// onboarding shell from web bundles. Mounted globally so the re-engagement
// card's "Show me how" CTA can enqueue it from any route (single consumer).
// Gate the whole dynamic() call: gating only its loader leaves generated module
// metadata pointing at a desktop chunk that the web build has removed.
const DesktopDictationOnboardingModal = isTauriBuild
  ? dynamic(
      () =>
        import('@/features/dictation/onboarding/desktopDictationOnboardingModal').then(
          (mod) => mod.DesktopDictationOnboardingModal
        ),
      { ssr: false }
    )
  : () => null;

// Same Tauri-only drop as DesktopDictationOnboardingModal so the hero PNG + SVG
// stay out of web bundles. Render-gated on the Flagsmith flag below.
const DictateReengagementCardPopup = isTauriBuild
  ? dynamic(
      () => import('@heidi/dictate').then((mod) => mod.DictateReengagementCardPopup),
      { ssr: false }
    )
  : () => null;

// UKI-218: Shadow Heidi is an entitlement-gated research-trial feature (GGC
// cohort). Lazy-load its surfaces so the feature's bundle (react-hook-form,
// zod resolver, research forms) stays out of the _app chunk and only loads for
// entitled users — see the `isShadowHeidiEnabled` render gate below.
const ClinicianResearchOnboardingModal = dynamic(
  () =>
    import('@/features/shadowHeidi/components/clinician-research-onboarding-modal').then(
      (mod) => ({ default: mod.ClinicianResearchOnboardingModal })
    ),
  { ssr: false }
);

const SessionMetadataModal = dynamic(
  () =>
    import('@/features/shadowHeidi/components/session-metadata-modal').then((mod) => ({
      default: mod.SessionMetadataModal,
    })),
  { ssr: false }
);

const ScribeOnboardingDictateModal = dynamic(
  () =>
    import('@/components/modal/scribe-onboarding-dictate-modal').then((mod) => ({
      default: mod.ScribeOnboardingDictateModal,
    })),
  { ssr: false }
);

const ShadowHeidiAutoStartController = dynamic(
  () =>
    import('@/features/shadowHeidi/components/shadow-heidi-auto-start-controller').then(
      (mod) => ({ default: mod.ShadowHeidiAutoStartController })
    ),
  { ssr: false }
);

export function CloudOnlyGlobalModals() {
  const dictateOnboardingStoreAccess = useDictateOnboardingStoreAccess();
  // Captures the user's last non-`/settings/*` route into sessionStorage so
  // legacy `/settings/*` hits can redirect somewhere meaningful.
  useTrackPreviousSettingsRoute();

  const loadDistributionTag = useSetAtom(loadDistributionTagAtom);
  const isEvidencePowerUserEnabled = useIsEvidencePowerUserEnabled();
  const isHeidi2OptInEnabled = useIsHeidi2OptInEnabled();
  const isCreditUsageEnabled = useIsCreditUsageSurfaceEnabled({
    isHeidi2OptInEnabled,
  });

  // Hydrate the persisted distribution marker on any route — users can land on
  // `/scribe/new-user` or other deep links without visiting `/` first.
  useEffect(() => {
    void loadDistributionTag();
  }, [loadDistributionTag]);

  const {
    data: userOrganisationInfo,
    isPending: isUserPending,
    isSuccess: isUserLoaded,
  } = useQueryUser((data) => data?.organisation_info);
  const areFlagsReady = usePosthogFlagsReady();

  const token = useSelector((state: RootState) => state.auth.token);
  const {
    data: kindeProperties,
    isPending,
    isError,
    error,
    isFetching,
  } = useQueryKindeProperties();
  // Ages that error against the current confirmed-online episode, and drives the
  // reconnect refetch React Query will not (WKWebView never reports the
  // reconnect). Without it the non-dismissible network-error modal re-opens on
  // reconnect off an error left over from the offline window.
  const { isKindePropertiesErrorStale } = useKindePropertiesReconnectRecovery({
    isError,
    error,
    isFetching,
  });

  // Kinde → Auth0 forced migration. Show only when the backend returned a live
  // ticket (created/refreshed), the user is still on Kinde (so the takeover
  // auto-dismisses once migration completes), and the migration flow can
  // complete on this platform.
  //
  // Desktop Kinde users have `authProvider === undefined` (no `<Auth>` wrapper
  // to set it); isKindeProvider resolves that. Once migration completes and
  // authProvider flips to AUTH0_DESKTOP, eligibility goes false.
  //
  // KindeMigrationModal is a forced, non-dismissible takeover whose Continue
  // launches AUTH0_APPLE_CONNECTION — which cannot complete on Windows desktop.
  // Its only other button logs the user out; with the Apple sign-in button
  // hidden on that platform they cannot get back in. isKindeAppleMigrationAvailable
  // prevents that lockout rather than causing one.
  const authProvider = useSelector((state: RootState) => state.auth.authProvider);
  const { data: appleMigration } = useQueryAppleMigrationTicket();
  const isEligibleForKindeMigrationModal = computeIsEligibleForKindeMigrationModal({
    appleMigrationStatus: appleMigration?.status,
    authProvider,
  });

  const isHealthNzMigrationPromptEnabled = useIsHealthNzMigrationPromptEnabled();

  const formViewModalOpen = useAtomValue(formViewModalOpenAtom);
  const trialExtensionModalOpen = useAtomValue(trialExtensionModalOpenAtom);
  const hasVisitedNewUserRoute = useAtomValue(hasVisitedNewUserRouteAtom);
  const hasCompletedOnboarding = useAtomValue(hasCompletedOnboardingAtom);

  const hasIntegration = useAtomValue(hasIntegrationAtom);
  const { userTier, newUserTier } = useQueryUserTier();
  const { data: user, isSuccess: isUserSuccess } = useQueryUser();

  const integrationAndNeedsUpgrade = computeIntegrationAndNeedsUpgrade({
    hasIntegration,
    userTier,
  });

  const router = useRouter();
  const {
    hasTeam,
    isJoinedTeamsPending,
    isJoinedTeamsFetching,
    isTeamAdmin,
    isNonAdminTeamMember,
    isTeamStripeManaged,
  } = useCombinedQueriesTeamGeneralInfo();
  const { data: joinableTeamsData } = useQuerySelfOnboardingTeams();
  const hasJoinableTeams = !isEmpty(joinableTeamsData?.data.joinable_teams);
  const canManageBilling =
    (!hasTeam || !!isTeamAdmin) && !isJoinedTeamsPending && !isJoinedTeamsFetching;
  const isPendingTeamInvitePromptEnabled = useIsPendingTeamInvitePromptEnabled();
  // A user who already holds a team can never see this prompt, so don't ask.
  const isPendingTeamInviteEligible =
    !hasTeam && !isJoinedTeamsPending && isPendingTeamInvitePromptEnabled;
  const { data: pendingInvitations } = useQueryPendingTeamInvitations({
    enabled: isPendingTeamInviteEligible,
  });
  const hasPendingTeamInvitations =
    (pendingInvitations?.pending_invitations.length ?? 0) > 0;
  const isMobile = useIsMobile();
  const isPatientListEnabled = useIsPatientListEnabled();
  // The card and spotlight both point at surfaces an EHR launch removes. Gated
  // at eligibility, not at render, so the once-per-user `seen_modals` stamp
  // stays unspent and the card still shows outside a launch.
  const isEhrLaunch = useIsEhrLaunch();
  const canSurfacePatientListNudges = isPatientListEnabled && !isEhrLaunch;
  const isSessionPageV2Enabled = useIsSessionPageV2Enabled();
  const canPromptSessionMergeOnboarding = useCanPromptSessionMergeOnboarding();
  const hasSessionMergeTab = useOpenSessionHasMergeTab();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const isProjectsEnabled = useIsProjectsEnabled();
  const isShareSessionAnnounceSpotlightEnabled =
    useIsShareSessionAnnounceSpotlightEnabled();
  const isAutoLinkSessionsEnabled = useIsAutoLinkSessionsEnabled();
  const isDesktopDictationFlagEnabled = useIsDesktopDictationFlagEnabled();
  const isWebDictationFlagEnabled = useIsWebDictationFlagEnabled();
  const isWebDictationOnboardingFlagEnabled = useIsWebDictationOnboardingFlagEnabled();
  const isDictateReengagementCardFlagEnabled = useIsDictateReengagementCardEnabled();
  const isDictateDeviceDetectedPopupFlagEnabled =
    useIsDictateDeviceDetectedPopupEnabled();
  const isDictateDeviceSetupFlagEnabled = useIsDictateDeviceSetupEnabled();
  const isFirmwareStartupCheckEnabled = useIsFirmwareStartupCheckEnabled();
  const { firmwareUpdateAvailable, firmwareUpdateStatus } = useChronicleDeviceStatus();
  const isRecording = useSelector((state: RootState) => state.timerModel.isRunning);
  const isClinicalRecordingActive = useIsClinicalRecordingActive(isRecording);
  // In-session Ask Heidi chat open (panel, drawer or AI column). SCR-7926: the
  // scribe-layout CSAT popover defers while this is true so it never stacks on
  // Ask Heidi's own "How did Heidi do?" prompt.
  const isAskHeidiOpen = useAtomValue(evidenceSidebarOpenAtom);
  // Single source of truth for desktop connectivity. Online-account / onboarding
  // modals are pessimistic: show only on a *confirmed* online reading. `pending`
  // (cold boot, connectivity unresolved) and `offline` both suppress them — no
  // onboarding flash on an offline relaunch, and no premature modal while the
  // probe is still resolving. Web always reads `online`, so web behaviour is
  // unchanged.
  const isDesktopConnectivityOnline = useDesktopConnectivity() === 'online';
  const { hasEvidenceAccess } = useEvidenceAccess();
  // UKI-218: gate the lazy-loaded Shadow Heidi surfaces so their chunk is only
  // fetched for entitled (GGC) users. The components also self-gate internally.
  const isShadowHeidiEnabled = useIsShadowHeidiEnabled();

  const isLinkShareAvailable = useIsLinkSharingAllowed();

  const DAYS_FOR_USER_ACCOUNT_MATURITY = 7;
  const isUserAtLeastAWeekOld = user?.created_at
    ? Date.now() - new Date(user.created_at).getTime() >=
      DAYS_FOR_USER_ACCOUNT_MATURITY * 24 * 60 * 60 * 1000
    : false;
  // Patients GA intro modal targets established users, not brand-new signups.
  const DAYS_FOR_PATIENT_LIST_INTRO_MATURITY = 14;
  const isUserAtLeastTwoWeeksOld = user?.created_at
    ? Date.now() - new Date(user.created_at).getTime() >=
      DAYS_FOR_PATIENT_LIST_INTRO_MATURITY * 24 * 60 * 60 * 1000
    : false;
  // Session list spotlight tour targets users past their first few
  // days, not brand-new signups.
  const DAYS_FOR_SESSION_LIST_SPOTLIGHT_MATURITY = 3;
  const isUserAtLeastThreeDaysOld = user?.created_at
    ? Date.now() - new Date(user.created_at).getTime() >=
      DAYS_FOR_SESSION_LIST_SPOTLIGHT_MATURITY * 24 * 60 * 60 * 1000
    : false;
  const showSessionListSpotlight = isNewSessionListEnabled && isUserAtLeastThreeDaysOld;
  const showRoutinesSpotlight = isProjectsEnabled;
  const heidi2ExploreLaterState = useHeidi2ExploreLaterState();
  const showHeidi2EvidenceTourSpotlight = computeShouldQueueHeidi2EvidenceTour({
    ...heidi2ExploreLaterState,
    step: useAtomValue(heidi2EvidenceTourStepAtom),
    isOnWorkHome: router.pathname.startsWith(WORK_CHAT_HOME_PATH),
    isRailAnchorMounted: useAtomValue(heidi2EvidenceTourRailMountedAtom),
    isHeadingHome: useAtomValue(heidi2EvidenceTourHeadingHomeAtom),
    isAddMenuMounted: useAtomValue(heidi2EvidenceTourAddMenuMountCountAtom) > 0,
  });
  // Drive the session list spotlight tour: auto-starts at step 1 once
  // NEW_SESSION_UPLOAD_SPOTLIGHT reaches the front of the modal queue. The step
  // components are anchored within the session list itself, not here.
  useSessionListSpotlightTour();
  // Same pattern for the Routines onboarding tour (T4, Work /scribe/chat/projects).
  useRoutinesSpotlightTour();

  const showScribeLayoutCsatPopover = isScribeLayoutCsatEligible({
    isLayoutEnabled: isSessionPageV2Enabled,
    isManaged: !!user?.is_managed,
    tourFirstSeenAt:
      user?.seen_modals?.[ModalId.SCRIBE_LAYOUT_V2_TOUR]?.first_seen_at ?? undefined,
  });

  // Check if current route has sidebar content for menu coachmark tour
  const hasSidebarRoute = ROUTE_TO_SIDEBAR_CONTENT.some((entry) =>
    router.pathname.startsWith(entry.pathPrefix)
  );

  const isUserSpecialtyEmpty =
    user && isEmpty(user?.specialty_id) && isEmpty(user?.other_specialty);

  const { isWidgetAuthFlow } = useIsWidgetAuthFlow();

  const shouldShowLegacyOnboardingDetailsModal =
    computeShouldShowLegacyOnboardingDetailsModal({
      isUserSuccess,
      hasRegion: !!kindeProperties?.region,
      pathname: router.pathname,
      integrationAndNeedsUpgrade,
      isDesktopConnectivityOnline,
      hasToken: !!token,
      isKindePropertiesPending: isPending,
      isKindePropertiesError: isError,
    });

  // Used for old (non-queued) flow
  const shouldShowUserSelfOnboardingModal = computeShouldShowUserSelfOnboardingModal({
    canSelfOnboardTeam: !!user?.can_self_onboard_team,
    hasTeam,
    hasJoinableTeams,
    isUserSpecialtyEmpty: !!isUserSpecialtyEmpty,
    isUserFirstNameEmpty: isEmpty(user?.first_name),
    isUserLastNameEmpty: isEmpty(user?.last_name),
    pathname: router.pathname,
    isWidgetAuthFlow,
    isDesktopConnectivityOnline,
    hasToken: !!token,
    isKindePropertiesPending: isPending,
    isKindePropertiesError: isError,
    isJoinedTeamsPending,
  });

  const shouldShowOnboardingModal = computeShouldShowOnboardingModal({
    isJoinedTeamsPending,
    hasTeam,
    canSelfOnboardTeam: !!user?.can_self_onboard_team,
    hasRegion: !!kindeProperties?.region,
    hasToken: !!token,
    isUserLoaded,
    userOrganisationInfo,
    integrationAndNeedsUpgrade,
    hasVisitedNewUserRoute,
    hasCompletedOnboarding,
    // Only the v2 flow resumes the in-app steps; with it off, a pending user
    // keeps the legacy survey. Undecided until the flags load.
    isOnboardingPending:
      !!user?.onboarding_pending_at && (!areFlagsReady || IS_ONBOARDING_V2_TREATMENT()),
    isDesktopConnectivityOnline,
    pathname: router.pathname,
  });

  const {
    hasTrialExpired: hasEvidenceTrialEnded,
    trialDaysRemaining: evidenceTrialDaysRemaining,
    isInFreeTrial: isInEvidenceTrial,
    isTrialCancelledByUser,
  } = useEvidenceBillingInfo();
  const { data: userBillingInfo, isSuccess: isBillingInfoLoaded } =
    useQueryUserBillingInfo();
  const isTeamHeidi2AccessControlsEnabled = useIsTeamHeidi2AccessControlsEnabled();
  const showHeidi2ConfirmAccessModal = computeShouldShowHeidi2ConfirmAccessModal({
    isTeamHeidi2AccessControlsEnabled,
    isTeamAdmin: !!isTeamAdmin,
    heidiVersion: userBillingInfo?.heidi_version,
    welcomeOutcome: heidi2ExploreLaterState.welcomeOutcome,
  });
  const { data: creditAllowance } = useQueryCreditAllowance(isCreditUsageEnabled);
  const {
    clinicianTrialEnded,
    isClinicianTrialUser,
    isClinicianPracticeTrialUser,
    isPracticeTrialUser,
    isInReverseTrial,
    clinicianTrialDaysLeft,
    clinicianTrialAvailable,
    isFakeClinicianTrial,
    previousStatusBeforeClinicianTrial,
  } = useReverseTrial();
  // GRO-3284: suppress the gamified trial in Arm B, and in Arm C until all
  // coaching-sidebar tasks are complete.
  const isGamifyArmAllowed = useIsGamifyTrialArmAllowed();

  const isScribePlusUser = user?.signup_source === SignupSource.scribe_plus;
  const shouldShowReverseTrialModals = computeShouldShowReverseTrialModals({
    isInReverseTrial: !!isInReverseTrial,
    isWidgetAuthFlow: !!isWidgetAuthFlow,
    isEvidenceLaunchTrialAndUpgradeFlowsEnabled:
      !!IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED(),
    isScribePlusUser,
  });

  const setAppStateForModals = useSetAtom(appStateForModalsAtom);
  const isDictationEnabled = useIsDictationEnabled();
  const setFirmwareStartupSeen = useSetAtom(firmwareStartupSeenAtom);
  const isOnboardingSubmitInProgress = useAtomValue(isOnboardingSubmitInProgressAtom);
  const pendingPracticeTrialModal = useAtomValue(practiceTrialModalPendingAtom);
  const setShouldShowAffiliateLinkModal = useSetAtom(shouldShowAffiliateLinkModalAtom);
  const hasShownAffiliateLinkModal = useAtomValue(hasShownAffiliateLinkModalAtom);
  const setHasShownAffiliateLinkModal = useSetAtom(hasShownAffiliateLinkModalAtom);
  const intl = useIntl();

  // Reset per-session firmware guard on sign-out so the startup check
  // re-triggers on the next sign-in.
  useEffect(() => {
    if (!token) {
      setFirmwareStartupSeen(false);
    }
  }, [token, setFirmwareStartupSeen]);

  useEffect(() => {
    if (router.isReady && !hasShownAffiliateLinkModal) {
      const showReferral = router.query.show_referral === 'true';
      // Enqueue is locale-gated — AffiliateLinkModal suppresses for ja-JP, and
      // a locale-blind enqueue would stall the launch queue head-of-line.
      if (showReferral && isReferralProgramAvailableForLocale(intl.locale)) {
        setShouldShowAffiliateLinkModal(true);
        setHasShownAffiliateLinkModal(true);
      }
    }
  }, [
    router.isReady,
    router.query.show_referral,
    hasShownAffiliateLinkModal,
    intl.locale,
    setShouldShowAffiliateLinkModal,
    setHasShownAffiliateLinkModal,
  ]);

  const showEvidencePostUpgradeWelcomePopup =
    hasEvidenceAccess &&
    (userTier === SubscriptionTier.CLINICIAN_PRO ||
      userTier === SubscriptionTier.CLINICIAN_PRO_PLUS) &&
    clinicianTrialEnded &&
    isPreviousTrialNinetyDayTrial(previousStatusBeforeClinicianTrial);

  const showClinicianTrialEndedModal = !!(
    isBillingInfoLoaded &&
    !!userTier &&
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() &&
    isPreviousTrialNinetyDayTrial(previousStatusBeforeClinicianTrial) &&
    clinicianTrialEnded &&
    (userTier === SubscriptionTier.PRO || userTier === SubscriptionTier.PRO_PLUS)
  );

  // Patients nav spotlight: show once the user saw the introduction modal 2+ days
  // ago. The intro modal's queue entry (`seen_modals`) carries `first_seen_at`,
  // so no dedicated user field is needed. Under the local force-flag the delay
  // collapses to 0 so the modal → spotlight flow can be smoke-tested in one sit.
  const DAYS_BEFORE_PATIENT_LIST_NAV_SPOTLIGHT =
    process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true' ? 0 : 2;
  const patientListIntroSeenAt =
    user?.seen_modals?.[PATIENT_LIST_INTRODUCTION_MODAL_ID]?.first_seen_at;
  const showPatientListNavSpotlight = !!(
    canSurfacePatientListNudges &&
    patientListIntroSeenAt &&
    differenceInDays(new Date(), new Date(patientListIntroSeenAt)) >=
      DAYS_BEFORE_PATIENT_LIST_NAV_SPOTLIGHT
  );

  // Scribe cross-sell: desktop dictate users on the logs page after enough
  // dictations. Same `IS_DICTATION_LOGS_ENABLED` umbrella as the logs surface.
  // Gate the query to that cohort so GlobalModalsWrapper doesn't fetch logs
  // for every authenticated user (old launcher was also global, but un-gated).
  const MIN_DICTATIONS_FOR_SCRIBE_CROSS_SELL = 20;
  const canEvaluateScribeCrossSell =
    IS_DICTATION_LOGS_ENABLED() &&
    isTauri() &&
    user?.signup_source === SignupSource.dictate &&
    router.pathname === '/dictation-logs';
  const { total: dictationLogsTotal } = useDictationLogs({
    enabled: canEvaluateScribeCrossSell,
  });
  const showScribeCrossSellSpotlight =
    canEvaluateScribeCrossSell &&
    (dictationLogsTotal ?? 0) > MIN_DICTATIONS_FOR_SCRIBE_CROSS_SELL;

  // Trial-end usage stats power the low-vs-high-usage matrix: low-usage admins
  // see the soft-touch discreet card first; high-usage users see the big modal
  // directly. Fetch as soon as the user is eligible for the trial-end
  // experience so the queue can decide which modal to enqueue without waiting
  // for the big modal to open. See `launchModalsConfigAtom` for the matrix.
  //
  // Do not enqueue either surface until stats settle (`isTrialEndUsageStatsReady`);
  // while pending, `(count ?? 0) < 5` reads as low-usage and would queue the
  // discreet card for high-usage users, and defaulting to high-usage would queue
  // the big modal for low-usage users (GRO-3172).
  const { isLowUsage, isUsageStatsPending } = useTrialRecapUsageStats(
    showClinicianTrialEndedModal
  );
  const isTrialEndUsageStatsReady = !isUsageStatsPending;
  const isTrialEndLowUsage = isLowUsage;

  const showLegacyTrialEndModal = computeShowLegacyTrialEndModal({
    isBillingInfoLoaded,
    hasEvidenceTrialEnded,
    isTrialCancelledByUser,
    clinicianTrialEnded,
    userTier,
  });

  const isInScribeClinicianTrial =
    (!!isClinicianTrialUser || !!isClinicianPracticeTrialUser) && !clinicianTrialEnded;

  const consumedCreditAllowanceThresholds = useAtomValue(
    creditAllowanceThresholdConsumedAtom
  );
  const isWorkspaceIdSettled = useWorkspaceIdSettledForCreditUsagePopoverLaunch();

  const shouldQueueSideNavCreditUsageThresholdPopover = useMemo(
    () =>
      computeShouldQueueSideNavCreditUsageThresholdPopover(
        {
          isHeidi2OptInEnabled,
          isUserManaged: !!user?.is_managed,
          newUserTier,
          isWorkspaceIdSettled,
          isAppleCompliantBuild,
        },
        userBillingInfo,
        creditAllowance ?? null,
        consumedCreditAllowanceThresholds
      ),
    [
      consumedCreditAllowanceThresholds,
      creditAllowance,
      isHeidi2OptInEnabled,
      isWorkspaceIdSettled,
      newUserTier,
      user?.is_managed,
      userBillingInfo,
    ]
  );

  useEffect(() => {
    setAppStateForModals((prev) => ({
      ...prev,
      hasRegion: !!kindeProperties?.region,
      hasToken: !!token,
      hasOrganisationInfo: !isEmpty(userOrganisationInfo),
      isUserPending,
      isUserDataLoaded: isUserSuccess,
      isManaged: !!user?.is_managed,
      pathname: router.pathname,
      hasIntegration,
      userTier: userTier || null,
      isNewUser: hasVisitedNewUserRoute,
      // Self-onboarding modal state
      canSelfOnboardTeam: !!user?.can_self_onboard_team,
      hasJoinedTeams: hasTeam,
      isUserSpecialtyEmpty:
        !!user && isEmpty(user?.specialty_id) && isEmpty(user?.other_specialty),
      isUserFirstNameEmpty: isEmpty(user?.first_name),
      isUserLastNameEmpty: isEmpty(user?.last_name),
      isJoinedTeamsPending,
      hasPendingTeamInvitations,
      canManageBilling,
      isKindePropertiesPending: isPending,
      isKindePropertiesError: isError,
      isKindePropertiesErrorStale,
      isDesktopConnectivityOnline,
      userSignupSource: user?.signup_source ?? SignupSource.scribe,
      hasEvidenceTrialEnded,
      hasClinicianTrialEnded: clinicianTrialEnded,
      previousStatusBeforeClinicianTrial: previousStatusBeforeClinicianTrial ?? null,
      showEvidencePostUpgradeWelcomePopup,
      showClinicianTrialEndedModal,
      isTrialEndLowUsage,
      isTrialEndUsageStatsReady,
      isNonAdminTeamMember: !!isNonAdminTeamMember,
      showEndTrialModal: showLegacyTrialEndModal,
      evidenceTrialDaysRemaining,
      clinicianTrialDaysLeft,
      isInEvidenceTrial: !!isInEvidenceTrial,
      isInScribeClinicianTrial,
      isNewUserAfterEvidenceLaunch: !!user?.is_new_user_after_evidence_launch,
      isTeamAdmin: !!isTeamAdmin,
      isClinicianPracticeUser: newUserTier === SubscriptionTier.CLINICIAN_PRO_PLUS,
      isClinicianUser: newUserTier === SubscriptionTier.CLINICIAN_PRO,
      hasEvidenceBillingHistory:
        !!userBillingInfo?.evidence_billing_state?.has_evidence_billing_history,
      isOnboardingSubmitInProgress,
      isMobile,
      hasSidebarRoute,
      clinicianTrialAvailable,
      organisationSize: userOrganisationInfo?.size ?? null,
      pendingPracticeTrialModal,
      isFakeClinicianTrial,
      isShareSessionAnnounceSpotlightEnabled,
      isAutoLinkSessionsEnabled,
      isLinkShareAvailable,
      hasEvidenceAccess,
      isUserAtLeastAWeekOld,
      isTauri: isTauri(),
      isDesktopDictationFlagEnabled,
      isWebDictationFlagEnabled,
      isWebDictationOnboardingFlagEnabled,
      isDictateReengagementCardFlagEnabled,
      isDictateDeviceDetectedPopupFlagEnabled,
      isDictateDeviceSetupFlagEnabled,
      isDictationEnabled,
      firmwareUpdateAvailable,
      firmwareUpdateStatus,
      isRecording,
      isAskHeidiOpen,
      isFirmwareStartupCheckEnabled,
      isTeamStripeManaged: !!isTeamStripeManaged,
      canSurfacePatientListNudges,
      canPromptSessionMergeOnboarding,
      hasSessionMergeTab,
      isClinicalRecordingActive,
      isUserManaged: !!user?.is_managed,
      isUserAtLeastTwoWeeksOld,
      showPatientListNavSpotlight,
      showSessionListSpotlight,
      showRoutinesSpotlight,
      showHeidi2EvidenceTourSpotlight,
      showHeidi2ConfirmAccessModal,
      showScribeCrossSellSpotlight,
      showScribeLayoutCsatPopover,
      isEligibleForKindeMigrationModal,
      isHealthNzMigrationPromptEnabled,
      isHeidi2OptInEnabled,
      shouldQueueSideNavCreditUsageThresholdPopover,
    }));
  }, [
    kindeProperties?.region,
    token,
    userOrganisationInfo,
    isUserPending,
    isUserSuccess,
    router.pathname,
    hasIntegration,
    userTier,
    hasVisitedNewUserRoute,
    setAppStateForModals,
    user?.can_self_onboard_team,
    hasTeam,
    user,
    isJoinedTeamsPending,
    hasPendingTeamInvitations,
    canManageBilling,
    isPending,
    isError,
    isKindePropertiesErrorStale,
    isDesktopConnectivityOnline,
    hasEvidenceTrialEnded,
    isTrialCancelledByUser,
    clinicianTrialEnded,
    previousStatusBeforeClinicianTrial,
    showEvidencePostUpgradeWelcomePopup,
    showClinicianTrialEndedModal,
    isTrialEndLowUsage,
    isTrialEndUsageStatsReady,
    isNonAdminTeamMember,
    showLegacyTrialEndModal,
    evidenceTrialDaysRemaining,
    clinicianTrialDaysLeft,
    isInEvidenceTrial,
    isInScribeClinicianTrial,
    user?.is_new_user_after_evidence_launch,
    isTeamAdmin,
    userBillingInfo?.evidence_billing_state?.has_evidence_billing_history,
    newUserTier,
    isOnboardingSubmitInProgress,
    isMobile,
    hasSidebarRoute,
    clinicianTrialAvailable,
    pendingPracticeTrialModal,
    isFakeClinicianTrial,
    isShareSessionAnnounceSpotlightEnabled,
    isAutoLinkSessionsEnabled,
    isLinkShareAvailable,
    hasEvidenceAccess,
    isUserAtLeastAWeekOld,
    isDesktopDictationFlagEnabled,
    isWebDictationFlagEnabled,
    isWebDictationOnboardingFlagEnabled,
    isDictateReengagementCardFlagEnabled,
    isDictateDeviceDetectedPopupFlagEnabled,
    isDictateDeviceSetupFlagEnabled,
    isDictationEnabled,
    firmwareUpdateAvailable,
    firmwareUpdateStatus,
    isRecording,
    isAskHeidiOpen,
    isFirmwareStartupCheckEnabled,
    isTeamStripeManaged,
    canSurfacePatientListNudges,
    canPromptSessionMergeOnboarding,
    hasSessionMergeTab,
    isClinicalRecordingActive,
    isUserAtLeastTwoWeeksOld,
    showPatientListNavSpotlight,
    showSessionListSpotlight,
    showRoutinesSpotlight,
    showHeidi2EvidenceTourSpotlight,
    showHeidi2ConfirmAccessModal,
    showScribeCrossSellSpotlight,
    showScribeLayoutCsatPopover,
    isEligibleForKindeMigrationModal,
    isHealthNzMigrationPromptEnabled,
    isHeidi2OptInEnabled,
    shouldQueueSideNavCreditUsageThresholdPopover,
  ]);

  return (
    <>
      <ScribeLayoutCsatPopoverHost />
      <LegacyOnboardingRequiredUserDetailsModal
        isOpen={shouldShowLegacyOnboardingDetailsModal}
        // non dismissable
        setOpen={() => {}}
      />
      {/* Old (non-queued) flow for self-onboarding modal */}
      {!IS_SELF_ONBOARDING_MODAL_QUEUED() && shouldShowUserSelfOnboardingModal && (
        <UserSelfOnboardingModal isOpen />
      )}
      <OnboardingModal
        isOpen={shouldShowOnboardingModal}
        userTier={userTier}
        backgroundBlur={false}
      />
      <IntegrationPaywall />
      <HeidiTogetherModal presetSelectedTier={SubscriptionTier.PRO_PLUS} />
      <SectionalWritebackInformModal />
      {formViewModalOpen && <FormViewModal />}

      <EndPromotedTrialModal />
      {shouldShowReverseTrialModals && (
        <>
          <TrialToPaidModal />
          <ExtendFreeTrialModal />
          <EndTrialModal />
          <HeidiProUpgradedSuccessful />
        </>
      )}
      {trialExtensionModalOpen &&
        isGamifyArmAllowed &&
        (isPracticeTrialUser ? (
          <GamifyTrialExtensionPracticeModal />
        ) : (
          <GamifyTrialExtensionTierModal />
        ))}
      <GamifyTrialExtensionInviteModal />

      {/* Queue-managed modals check out launchModalsConfigAtom.ts*/}
      <Heidi2ConfirmAccessModal />
      <ScribeOnboardingDictateModal />
      <NewInThisReleasePopup />
      {isDictateReengagementCardFlagEnabled && <DictateReengagementCardPopup />}
      <DesktopDictationOnboardingModal storeAccess={dictateOnboardingStoreAccess} />
      {canSurfacePatientListNudges && <PatientListIntroductionModal />}
      {IS_PRECHART_ENABLED() && <PrechartBetaIntroModal />}
      <EnableSessionMergeCardPopover />
      <EnableSessionMergeCardPopover
        modalId={SESSION_MERGE_ONBOARDING_SETTINGS_MODAL_ID}
      />
      <LazySessionMergeEducationModal />
      <SessionMergeTabCoachmark />
      <NetworkErrorModal />
      {isPendingTeamInviteEligible && <PendingTeamInviteModal />}
      {IS_SELF_ONBOARDING_MODAL_QUEUED() && isDesktopConnectivityOnline && (
        <UserSelfOnboardingModal />
      )}
      <FreeTrialModal />
      <AffiliateLinkModal />
      <EvidenceReferralModal />
      <GetMobileAppModalHost />
      <CustomTemplateOnboardingModal />

      {/* Handles importing shared templates after all modals are dismissed */}
      <PendingSharedTemplateImporter />

      {/* Evidence Plus upgrade retired (GRO-3609) — all CTAs open clinician atom */}
      {router.pathname !== '/dev/trial-modals' && <ClinicianUpgradeModal />}

      {isCreditUsageEnabled && router.pathname !== '/dev/trial-modals' && (
        <ClinicianMaxUpgradeModal />
      )}

      {IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() &&
        router.pathname !== '/dev/trial-modals' && (
          <>
            <TrialEndingSoonReminderModal />
            {IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() && (
              <>
                <TrialEndReminderPopup />
                <TrialEndDiscreetCard />
                <TrialRecapModal />
                <TeamTrialUpgradeRequestModal />
                <TrialUpgradeRequestCallbackModal />
                <EvidencePostUpgradeWelcomePopup />
                <EvidencePostUpgradeOnboardingModal />
              </>
            )}
            <PracticeUpgradeModal />
            <ClinicianTrialEndedModal />
            <TrialEndPlanSelectionModal />
            <ClinicianTrialModal />
            <PracticeTrialModal />
            <PracticeInviteTeamModal />
            <DictationPremiumTrialModal />
            <ScribeOnboardingModal />
            <DowngradeReconsiderModal />
            <DowngradeRetentionFlowModals />
            <PricingPlansModal />
            {isEvidencePowerUserEnabled && <EvidencePowerUserUpgradeModal />}
          </>
        )}

      <KindeMigrationModal />

      <HealthNzMigrationModal />

      {/* UKI-218: Shadow Heidi research-trial surfaces. Entitlement-gated to the
          GGC cohort and lazy-loaded (see the dynamic() declarations above) so the
          feature's bundle stays out of the _app chunk and only loads when enabled. */}
      {isShadowHeidiEnabled && (
        <>
          <ClinicianResearchOnboardingModal />
          <SessionMetadataModal />
          <ShadowHeidiAutoStartController />
        </>
      )}

      {IS_EVIDENCE_COPY_CONFIRMATION_ENABLED && <EvidenceCopyConfirmationPopover />}
    </>
  );
}

// Memo barrier: this hosts ~50 always-mounted modal subtrees. `GlobalModals`
// memoises too, but this component's own atom/query subscriptions re-render it
// independently of its parent, so the barrier stays here as well.
export default memo(CloudOnlyGlobalModals);
